import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { getCompanies } from '../utils/companyManagement';
export type UserRole = 'admin' | 'employee';
export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  username?: string; // stored for super admin identification
  company?: string;
  branch?: string;
  employeeId?: string;
  permissions?: {
    canOrder?: boolean;
    canViewReports?: boolean;
    canManageMenu?: boolean;
    canManageEmployees?: boolean;
    isManager?: boolean;
    tempPassword?: boolean;
  };
  adminPermissions?: {
    orders?: boolean;
    production?: boolean;
    labels?: boolean;
    claims?: boolean;
    companies?: boolean;
    employees?: boolean;
    menu?: boolean;
    promotions?: boolean;
    reports?: boolean;
    activityLog?: boolean;
    settings?: boolean;
    users?: boolean;
    chat?: boolean;
    delivery?: boolean;
    assignments?: boolean;
    assignedCompanies?: (number | string)[];
    assignedOrderIds?: string[];
  };
  granularPermissions?: Record<string, boolean>;
  assignedCompanies?: (number | string)[];
  assignedOrderIds?: string[];
  isDeliveryOnly?: boolean;
}
interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loginError: string | null;
  isLocked: boolean;
  lockoutRemaining: number;
  mustChangePassword: boolean;
  clearTempPassword: () => void;
  /** Impersonation */
  isImpersonating: boolean;
  impersonateUser: (adminUser: any) => void;
  impersonateEmployee: (employeeData: any, companies: any[]) => void;
  stopImpersonating: () => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Rate limiting configuration
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 60 * 1000; // 1 minute lockout
const LOCKOUT_STORAGE_KEY = 'criket_auth_lockout';
// Sanitize input - strip HTML tags and limit length
const sanitizeInput = (input: string, maxLength: number = 100): string => {
  return input.replace(/<[^>]*>/g, '').replace(/[<>"'&]/g, '').trim().slice(0, maxLength);
};
// Persistent rate limiting helpers
const getLockoutState = (): {
  attempts: number;
  lockoutUntil: number;
} => {
  try {
    const saved = localStorage.getItem(LOCKOUT_STORAGE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      if (state.lockoutUntil && state.lockoutUntil < Date.now()) {
        localStorage.removeItem(LOCKOUT_STORAGE_KEY);
        return {
          attempts: 0,
          lockoutUntil: 0
        };
      }
      return state;
    }
  } catch {}
  return {
    attempts: 0,
    lockoutUntil: 0
  };
};
const saveLockoutState = (attempts: number, lockoutUntil: number) => {
  localStorage.setItem(LOCKOUT_STORAGE_KEY, JSON.stringify({
    attempts,
    lockoutUntil
  }));
};
const IMPERSONATION_KEY = 'criket_impersonation_original';
/** Map raw admin DB user to a User session object (shared by login + impersonation) */
function mapAdminToUser(adminData: any): User {
  const rawPerms = adminData.permissions || {};
  const isSuperAdmin = adminData.username === 'admin';
  const granular = rawPerms.granular || null;
  const isDeliveryUser = !isSuperAdmin && (adminData.role === 'delivery' || rawPerms.role === 'delivery');
  let adminPerms: Record<string, boolean>;
  let granularPerms: Record<string, boolean> | undefined = undefined;
  if (isSuperAdmin) {
    adminPerms = {
      orders: true,
      production: true,
      labels: true,
      claims: true,
      companies: true,
      employees: true,
      menu: true,
      promotions: true,
      reports: true,
      activityLog: true,
      settings: true,
      users: true,
      chat: true,
      delivery: true,
      assignments: true
    };
  } else if (isDeliveryUser) {
    adminPerms = {
      orders: false,
      production: false,
      labels: false,
      claims: false,
      companies: false,
      employees: false,
      menu: false,
      promotions: false,
      reports: false,
      activityLog: false,
      settings: false,
      users: false,
      chat: false,
      delivery: false,
      assignments: false
    };
  } else if (granular) {
    adminPerms = {
      orders: granular['orders.view'] === true,
      production: granular['production.view'] === true,
      labels: granular['labels.view'] === true,
      claims: granular['claims.view'] === true,
      companies: granular['companies.view'] === true,
      employees: granular['employees.view'] === true,
      menu: granular['menu.view'] === true,
      promotions: granular['promotions.view'] === true,
      reports: granular['reports.view'] === true,
      activityLog: granular['activityLog.view'] === true,
      settings: granular['settings.view'] === true,
      users: granular['users.view'] === true,
      chat: granular['chat.view'] !== false,
      delivery: granular['delivery.view'] === true,
      assignments: granular['assignments.view'] === true
    };
    granularPerms = {
      ...granular
    };
  } else {
    adminPerms = {
      orders: rawPerms.canManageOrders !== false,
      production: rawPerms.canManageOrders !== false,
      labels: rawPerms.canManageOrders !== false,
      claims: rawPerms.canManageOrders !== false,
      companies: rawPerms.canManageCompanies !== false,
      employees: rawPerms.canManageEmployees !== false,
      menu: rawPerms.canManageMenu !== false,
      promotions: rawPerms.canManageMenu !== false,
      reports: rawPerms.canViewReports !== false,
      activityLog: rawPerms.canViewReports !== false,
      settings: rawPerms.canManageUsers !== false,
      users: rawPerms.canManageUsers !== false,
      chat: true,
      delivery: rawPerms.canManageOrders !== false,
      assignments: rawPerms.canManageOrders !== false
    };
  }
  const assignedCompanies = rawPerms.assignedCompanies || granular?.assignedCompanies || undefined;
  const assignedOrderIds = rawPerms.assignedOrderIds || granular?.assignedOrderIds || undefined;
  return {
    id: adminData.id,
    name: adminData.name,
    email: adminData.email,
    role: 'admin',
    username: adminData.username,
    adminPermissions: {
      ...adminPerms,
      assignedCompanies,
      assignedOrderIds
    },
    granularPermissions: granularPerms,
    assignedCompanies,
    assignedOrderIds,
    isDeliveryOnly: isDeliveryUser
  };
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = sessionStorage.getItem('criket_user_session');
    if (!savedUser) return null;
    try {
      const parsed = JSON.parse(savedUser);
      if (!parsed.id || !parsed.role || !parsed.name) {
        sessionStorage.removeItem('criket_user_session');
        return null;
      }
      return {
        ...parsed,
        permissions: parsed.permissions || undefined,
        adminPermissions: parsed.adminPermissions || undefined,
        granularPermissions: parsed.granularPermissions || undefined,
        assignedCompanies: parsed.assignedCompanies || undefined,
        assignedOrderIds: parsed.assignedOrderIds || undefined,
        isDeliveryOnly: parsed.isDeliveryOnly || undefined
      };
    } catch {
      sessionStorage.removeItem('criket_user_session');
      return null;
    }
  });
  const [isImpersonating, setIsImpersonating] = useState(() => !!sessionStorage.getItem(IMPERSONATION_KEY));
  const [loginError, setLoginError] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(() => {
    const savedUser = sessionStorage.getItem('criket_user_session');
    if (!savedUser) return false;
    try {
      const parsed = JSON.parse(savedUser);
      return parsed.permissions?.tempPassword === true;
    } catch {
      return false;
    }
  });
  const [loginAttempts, setLoginAttempts] = useState(() => getLockoutState().attempts);
  const [lockoutUntil, setLockoutUntil] = useState<number>(() => getLockoutState().lockoutUntil);
  const [lockoutRemaining, setLockoutRemaining] = useState(0);
  const lockoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  // STARTUP: Sync companies from Supabase to localStorage on mount
  // This ensures getCompanies() always has fresh data for resolving names
  useEffect(() => {
    const syncCompanies = async () => {
      if (!isSupabaseConfigured()) return;
      try {
        const companies = await supabaseClient.getCompanies();
        if (companies.length > 0) {
          localStorage.setItem('criket_companies', JSON.stringify(companies));
          // ALWAYS re-resolve company/branch for logged-in employees
          // This fixes stale sessions with "Sin empresa", "N/A", or any incorrect value
          const savedUser = sessionStorage.getItem('criket_user_session');
          if (savedUser) {
            try {
              const parsed = JSON.parse(savedUser);
              if (parsed.role === 'employee') {
                // Fetch employee data to get current company_id and branch_id from DB
                const employees = await supabaseClient.getEmployees();
                const emp = employees.find((e: any) => e.id === parsed.employeeId || e.id === parsed.id);
                if (emp && emp.company_id) {
                  const company = companies.find((c: any) => c.id === emp.company_id);
                  const branch = company?.branches?.find((b: any) => b.id === emp.branch_id);
                  const resolvedCompany = company?.name || parsed.company;
                  const resolvedBranch = branch?.name || parsed.branch;
                  // Update if different from what's stored
                  if (resolvedCompany !== parsed.company || resolvedBranch !== parsed.branch) {
                    const updatedUser = {
                      ...parsed,
                      company: resolvedCompany,
                      branch: resolvedBranch
                    };
                    sessionStorage.setItem('criket_user_session', JSON.stringify(updatedUser));
                    setUser(updatedUser);
                  }
                  // Also re-resolve permissions from DB if they're missing in session
                  if (!parsed.permissions && emp.permissions) {
                    const updatedUser = {
                      ...parsed,
                      company: resolvedCompany || parsed.company,
                      branch: resolvedBranch || parsed.branch,
                      permissions: emp.permissions
                    };
                    sessionStorage.setItem('criket_user_session', JSON.stringify(updatedUser));
                    setUser(updatedUser);
                  }
                }
              }
            } catch (e) {
              console.error('Error re-resolving employee company:', e);
            }
          }
        }
      } catch (e) {
        console.error('Error syncing companies:', e);
      }
    };
    syncCompanies();
  }, []);
  // STARTUP: Refresh admin permissions from DB on mount
  // This fixes stale sessions when permissions are changed by the main admin
  useEffect(() => {
    const refreshAdminPermissions = async () => {
      if (!user || user.role !== 'admin' || !isSupabaseConfigured()) return;
      try {
        const adminUsers = await supabaseClient.getAdminUsers();
        const currentAdmin = adminUsers.find((u: any) => u.id === user.id);
        if (!currentAdmin) return;
        const rawPerms = currentAdmin.permissions || {};
        const isSuperAdmin = currentAdmin.username === 'admin';
        const granular = rawPerms.granular || null;
        const isDeliveryUser = !isSuperAdmin && (currentAdmin.role === 'delivery' || rawPerms.role === 'delivery');
        let adminPerms: Record<string, boolean>;
        let granularPerms: Record<string, boolean> | undefined = undefined;
        if (isSuperAdmin) {
          adminPerms = {
            orders: true,
            production: true,
            labels: true,
            claims: true,
            companies: true,
            employees: true,
            menu: true,
            promotions: true,
            reports: true,
            activityLog: true,
            settings: true,
            users: true,
            chat: true
          };
          // Super admin: no granular restrictions
        } else if (isDeliveryUser) {
          adminPerms = {
            orders: false,
            production: false,
            labels: false,
            claims: false,
            companies: false,
            employees: false,
            menu: false,
            promotions: false,
            reports: false,
            activityLog: false,
            settings: false,
            users: false,
            chat: false
          };
        } else if (granular) {
          adminPerms = {
            orders: granular['orders.view'] === true,
            production: granular['production.view'] === true,
            labels: granular['labels.view'] === true,
            claims: granular['claims.view'] === true,
            companies: granular['companies.view'] === true,
            employees: granular['employees.view'] === true,
            menu: granular['menu.view'] === true,
            promotions: granular['promotions.view'] === true,
            reports: granular['reports.view'] === true,
            activityLog: granular['activityLog.view'] === true,
            settings: granular['settings.view'] === true,
            users: granular['users.view'] === true,
            chat: granular['chat.view'] !== false
          };
          granularPerms = {
            ...granular
          };
        } else {
          adminPerms = {
            orders: rawPerms.canManageOrders !== false,
            production: rawPerms.canManageOrders !== false,
            labels: rawPerms.canManageOrders !== false,
            claims: rawPerms.canManageOrders !== false,
            companies: rawPerms.canManageCompanies !== false,
            employees: rawPerms.canManageEmployees !== false,
            menu: rawPerms.canManageMenu !== false,
            promotions: rawPerms.canManageMenu !== false,
            reports: rawPerms.canViewReports !== false,
            activityLog: rawPerms.canViewReports !== false,
            settings: rawPerms.canManageUsers !== false,
            users: rawPerms.canManageUsers !== false,
            chat: true
          };
        }
        const assignedCompanies = rawPerms.assignedCompanies || granular?.assignedCompanies || undefined;
        const assignedOrderIds = rawPerms.assignedOrderIds || granular?.assignedOrderIds || undefined;
        setUser(prev => {
          if (!prev || prev.role !== 'admin') return prev;
          return {
            ...prev,
            adminPermissions: {
              ...adminPerms,
              assignedCompanies,
              assignedOrderIds
            },
            granularPermissions: granularPerms,
            assignedCompanies,
            assignedOrderIds,
            isDeliveryOnly: isDeliveryUser
          };
        });
      } catch (e) {
        // Silently ignore — user might not have access to list_admin_users RPC
        console.warn('[Auth] Could not refresh admin permissions:', e);
      }
    };
    refreshAdminPermissions();
  }, []); // Only on mount — fixes stale session after permission changes
  useEffect(() => {
    if (lockoutUntil > Date.now()) {
      const updateCountdown = () => {
        const remaining = Math.max(0, Math.ceil((lockoutUntil - Date.now()) / 1000));
        setLockoutRemaining(remaining);
        if (remaining <= 0) {
          setLoginAttempts(0);
          setLockoutUntil(0);
          localStorage.removeItem(LOCKOUT_STORAGE_KEY);
          if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);
        }
      };
      updateCountdown();
      lockoutTimerRef.current = setInterval(updateCountdown, 1000);
      return () => {
        if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);
      };
    }
  }, [lockoutUntil]);
  useEffect(() => {
    if (user) {
      const safeUser = {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        username: user.username,
        company: user.company,
        branch: user.branch,
        employeeId: user.employeeId,
        permissions: user.permissions,
        adminPermissions: user.adminPermissions,
        granularPermissions: user.granularPermissions,
        assignedCompanies: user.assignedCompanies,
        assignedOrderIds: user.assignedOrderIds,
        isDeliveryOnly: user.isDeliveryOnly
      };
      sessionStorage.setItem('criket_user_session', JSON.stringify(safeUser));
    } else {
      sessionStorage.removeItem('criket_user_session');
    }
  }, [user]);
  const isLocked = lockoutUntil > Date.now();
  const clearTempPassword = useCallback(() => {
    setMustChangePassword(false);
    if (user) {
      const updatedUser = {
        ...user,
        permissions: {
          ...user.permissions,
          tempPassword: false
        }
      };
      setUser(updatedUser);
      sessionStorage.setItem('criket_user_session', JSON.stringify(updatedUser));
    }
  }, [user]);
  const impersonateUser = useCallback((adminUser: any) => {
    // Save current super admin session before switching
    const currentSession = sessionStorage.getItem('criket_user_session');
    if (currentSession) {
      sessionStorage.setItem(IMPERSONATION_KEY, currentSession);
    }
    const impersonatedUser = mapAdminToUser(adminUser);
    setUser(impersonatedUser);
    setIsImpersonating(true);
  }, []);
  const impersonateEmployee = useCallback((employeeData: any, companies: any[]) => {
    // Save current admin session before switching
    const currentSession = sessionStorage.getItem('criket_user_session');
    if (currentSession) {
      sessionStorage.setItem(IMPERSONATION_KEY, currentSession);
    }
    // Resolve company and branch names
    const company = companies.find((c: any) => c.id === employeeData.companyId || c.id === employeeData.company_id);
    const branch = company?.branches?.find((b: any) => b.id === employeeData.branchId || b.id === employeeData.branch_id);
    const employeeUser: User = {
      id: employeeData.id,
      name: employeeData.name,
      email: employeeData.email,
      role: 'employee',
      company: company?.name || 'N/A',
      branch: branch?.name || 'N/A',
      employeeId: employeeData.id,
      permissions: employeeData.permissions || {
        canOrder: true,
        canViewReports: false,
        canManageMenu: false,
        canManageEmployees: false,
        isManager: false,
        tempPassword: false
      }
    };
    setUser(employeeUser);
    setIsImpersonating(true);
    setMustChangePassword(false);
  }, []);
  const stopImpersonating = useCallback(() => {
    const originalSession = sessionStorage.getItem(IMPERSONATION_KEY);
    if (originalSession) {
      try {
        const originalUser = JSON.parse(originalSession);
        setUser(originalUser);
        sessionStorage.removeItem(IMPERSONATION_KEY);
        setIsImpersonating(false);
      } catch {
        sessionStorage.removeItem(IMPERSONATION_KEY);
        setUser(null);
        setIsImpersonating(false);
      }
    } else {
      setIsImpersonating(false);
    }
  }, []);
  const handleFailedAttempt = () => {
    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);
    if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
      const newLockout = Date.now() + LOCKOUT_DURATION_MS;
      setLockoutUntil(newLockout);
      saveLockoutState(newAttempts, newLockout);
      setLoginError('Cuenta bloqueada temporalmente. Espera 60 segundos.');
    } else {
      saveLockoutState(newAttempts, 0);
      setLoginError(`Credenciales incorrectas. ${MAX_LOGIN_ATTEMPTS - newAttempts} intentos restantes.`);
    }
  };
  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    setLoginError(null);
    if (isLocked) {
      setLoginError(`Demasiados intentos. Espera ${lockoutRemaining} segundos.`);
      return false;
    }
    const cleanUsername = sanitizeInput(username, 50);
    const cleanPassword = password.trim().slice(0, 128);
    if (!cleanUsername || !cleanPassword) {
      setLoginError('Usuario y contraseña son requeridos');
      return false;
    }
    if (!isSupabaseConfigured()) {
      setLoginError('Base de datos no configurada');
      return false;
    }
    try {
      const authResult = await supabaseClient.authenticateUser(cleanUsername, cleanPassword);
      if (authResult) {
        setLoginAttempts(0);
        localStorage.removeItem(LOCKOUT_STORAGE_KEY);
        if (authResult.role === 'admin') {
          const rawPerms = authResult.permissions || {};
          const isSuperAdmin = authResult.username === 'admin';
          const granular = rawPerms.granular || null;
          const isDeliveryUser = !isSuperAdmin && rawPerms.role === 'delivery';
          let adminPerms: Record<string, boolean>;
          let granularPerms: Record<string, boolean> | undefined = undefined;
          if (isSuperAdmin) {
            adminPerms = {
              orders: true,
              production: true,
              labels: true,
              claims: true,
              companies: true,
              employees: true,
              menu: true,
              promotions: true,
              reports: true,
              activityLog: true,
              settings: true,
              users: true,
              chat: true,
              delivery: true,
              assignments: true
            };
          } else if (isDeliveryUser) {
            adminPerms = {
              orders: false,
              production: false,
              labels: false,
              claims: false,
              companies: false,
              employees: false,
              menu: false,
              promotions: false,
              reports: false,
              activityLog: false,
              settings: false,
              users: false,
              chat: false,
              delivery: false,
              assignments: false
            };
          } else if (granular) {
            adminPerms = {
              orders: granular['orders.view'] === true,
              production: granular['production.view'] === true,
              labels: granular['labels.view'] === true,
              claims: granular['claims.view'] === true,
              companies: granular['companies.view'] === true,
              employees: granular['employees.view'] === true,
              menu: granular['menu.view'] === true,
              promotions: granular['promotions.view'] === true,
              reports: granular['reports.view'] === true,
              activityLog: granular['activityLog.view'] === true,
              settings: granular['settings.view'] === true,
              users: granular['users.view'] === true,
              chat: granular['chat.view'] !== false,
              delivery: granular['delivery.view'] === true,
              assignments: granular['assignments.view'] === true
            };
            granularPerms = {
              ...granular
            };
          } else {
            adminPerms = {
              orders: rawPerms.canManageOrders !== false,
              production: rawPerms.canManageOrders !== false,
              labels: rawPerms.canManageOrders !== false,
              claims: rawPerms.canManageOrders !== false,
              companies: rawPerms.canManageCompanies !== false,
              employees: rawPerms.canManageEmployees !== false,
              menu: rawPerms.canManageMenu !== false,
              promotions: rawPerms.canManageMenu !== false,
              reports: rawPerms.canViewReports !== false,
              activityLog: rawPerms.canViewReports !== false,
              settings: rawPerms.canManageUsers !== false,
              users: rawPerms.canManageUsers !== false,
              chat: true,
              delivery: rawPerms.canManageOrders !== false,
              assignments: rawPerms.canManageOrders !== false
            };
          }
          const assignedCompanies = rawPerms.assignedCompanies || rawPerms.granular?.assignedCompanies || undefined;
          const assignedOrderIds = rawPerms.assignedOrderIds || rawPerms.granular?.assignedOrderIds || undefined;
          setUser({
            id: authResult.id,
            name: authResult.name,
            email: authResult.email,
            role: 'admin',
            username: authResult.username,
            adminPermissions: {
              ...adminPerms,
              assignedCompanies,
              assignedOrderIds
            },
            granularPermissions: granularPerms,
            assignedCompanies,
            assignedOrderIds,
            isDeliveryOnly: isDeliveryUser
          });
          setMustChangePassword(false);
          return true;
        }
        // Employee - resolve company/branch names
        let companies = getCompanies();
        if (companies.length === 0) {
          try {
            const supabaseCompanies = await supabaseClient.getCompanies();
            if (supabaseCompanies.length > 0) {
              localStorage.setItem('criket_companies', JSON.stringify(supabaseCompanies));
              companies = supabaseCompanies;
            }
          } catch (e) {
            console.error('Error loading companies from Supabase:', e);
          }
        }
        const company = companies.find(c => c.id === authResult.company_id);
        const branch = company?.branches?.find((b: any) => b.id === authResult.branch_id);
        const isTempPassword = authResult.permissions?.tempPassword === true;
        setUser({
          id: authResult.id,
          name: authResult.name,
          email: authResult.email,
          role: 'employee',
          company: company?.name || 'N/A',
          branch: branch?.name || 'N/A',
          employeeId: authResult.id,
          permissions: authResult.permissions || {
            canOrder: true,
            canViewReports: false,
            canManageMenu: false,
            canManageEmployees: false,
            isManager: false,
            tempPassword: false
          }
        });
        setMustChangePassword(isTempPassword);
        return true;
      }
      handleFailedAttempt();
      return false;
    } catch (error) {
      setLoginError('Error al conectar con el servidor. Intenta nuevamente.');
      return false;
    }
  }, [loginAttempts, isLocked, lockoutRemaining]);
  const logout = useCallback(() => {
    setUser(null);
    setLoginError(null);
    setMustChangePassword(false);
    sessionStorage.removeItem('criket_user_session');
  }, []);
  return <AuthContext.Provider value={{
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loginError,
    isLocked,
    lockoutRemaining,
    mustChangePassword,
    clearTempPassword,
    isImpersonating,
    impersonateUser,
    impersonateEmployee,
    stopImpersonating
  }} data-id="element-1188">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}